import React from 'react';

import {BaseMigrationObjectPlan} from 'config-migration/types/MigrationObjects';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';
import uniqueId from 'util/uniqueId';

import {ObjectAction, ObjectActionType} from '../types/objectActions';

interface ActionDropdownProps {
  object: BaseMigrationObjectPlan;
  onUpdateObject: (object: BaseMigrationObjectPlan) => void;
  showActionInline?: boolean;
  showIgnore?: boolean;
  filteredAdditionalActions?: Array<ObjectAction>;
  disableDropdown?: boolean;
}

export function getActionDescription(action: ObjectAction): JSX.Element | string {
  if (action.type === ObjectActionType.CREATE) {
    return 'Create';
  }
  if (action.type === ObjectActionType.IGNORE) {
    return action.reason != null ? `Ignore (${action.reason})` : 'Ignore';
  } else {
    return (
      <span>
        Update <strong>{action.target.displayName}</strong>
        {action.target.archived && ` (Archived)`}
      </span>
    );
  }
}

export function getOptionValue(action: ObjectAction): string {
  return action.type === ObjectActionType.UPDATE ? action.target.apiIdentifier : action.type;
}

export function getActionOption(action: ObjectAction, onSelect: (action: ObjectAction) => void): JSX.Element {
  return (
    <Option
      value={getOptionValue(action)}
      onSelect={() => {
        onSelect(action);
      }}
      key={uniqueId('action-')}
    >
      {getActionDescription(action)}
    </Option>
  );
}

export default function ObjectActionDropdown(props: ActionDropdownProps): JSX.Element {
  const updateObject = (selectedAction: ObjectAction): void => {
    props.onUpdateObject({...props.object, selectedAction});
  };
  if (props.showActionInline) {
    return (
      <div className="u-mini-spacing-right u-flex-grow">
        {getActionDescription(props.object.selectedAction)}
      </div>
    );
  }
  return (
    <SelectDropdown
      className="u-mini-spacing-right u-flex-grow"
      disabled={props.disableDropdown}
      menu={
        <Menu
          height={Menu.HEIGHTS.MEDIUM}
          width={Menu.WIDTHS.FULL}
          value={getOptionValue(props.object.selectedAction)}
          scrollable={true}
        >
          <Section>
            {getActionOption(
              props.object.hasProvenanceUriMatch
                ? props.object.initialAction
                : {type: ObjectActionType.CREATE},
              updateObject
            )}
            {props.showIgnore && getActionOption({type: ObjectActionType.IGNORE}, updateObject)}
          </Section>
          <Section>
            {!props.object.hasProvenanceUriMatch &&
              (props.filteredAdditionalActions !== undefined
                ? props.filteredAdditionalActions.map((action) => getActionOption(action, updateObject))
                : props.object.additionalActions.map((action) => getActionOption(action, updateObject)))}
          </Section>
        </Menu>
      }
      displayName={getActionDescription(props.object.selectedAction)}
    />
  );
}
